/**
 * Generic WhiteBooks-backed compliance return viewer.
 * Renders GSTR-6, GSTR-9, GSTR-9C, ITC-04 by hitting the appropriate WhiteBooks
 * endpoint via our /api/gst-reports/* proxy. Requires a live session first.
 */
import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, AlertCircle } from "lucide-react";
import GstinSelector from "@/components/gst/GstinSelector";

const RETURN_CONFIG = {
  gstr6:  { title: "GSTR-6 · ISD Return",      endpoint: "gstr6",  sections: ["retsum","b2b","cdn","itc"] },
  gstr6a: { title: "GSTR-6A · Auto-drafted",   endpoint: "gstr6a", sections: ["b2b","cdn","b2ba","cdna"] },
  gstr9:  { title: "GSTR-9 · Annual Return",   endpoint: "gstr9",  sections: null, annual: true },
  gstr9c: { title: "GSTR-9C · Reconciliation", endpoint: "gstr9c", sections: null, annual: true },
  itc04:  { title: "ITC-04 · Job-Work",        endpoint: "itc04",  sections: null },
};

const MONTHS = [
  { v: "04", n: "Apr" }, { v: "05", n: "May" }, { v: "06", n: "Jun" },
  { v: "07", n: "Jul" }, { v: "08", n: "Aug" }, { v: "09", n: "Sep" },
  { v: "10", n: "Oct" }, { v: "11", n: "Nov" }, { v: "12", n: "Dec" },
  { v: "01", n: "Jan" }, { v: "02", n: "Feb" }, { v: "03", n: "Mar" },
];

const FY_LIST = (() => {
  const d = new Date();
  const startYr = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return Array.from({ length: 6 }, (_, i) => {
    const y = startYr - i;
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  });
})();

const nowMMYYYY = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, "0")}${d.getFullYear()}`;
};
const nowFy = () => {
  const d = new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
};

export default function ComplianceReturnPage({ returnKey }) {
  const cfg = RETURN_CONFIG[returnKey];
  const [gstin, setGstin] = useState(null);
  const [period, setPeriod] = useState(nowMMYYYY());
  const [fy, setFy] = useState(nowFy());
  const [mode, setMode] = useState(cfg?.annual ? "fy" : "period");
  const [section, setSection] = useState(cfg?.sections?.[0] || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setData(null); }, [returnKey]);

  if (!cfg) return <div>Unknown return type</div>;
  const isAnnual = !!cfg.annual;

  const fetchIt = async () => {
    if (!gstin) { toast.error("Pick a GSTIN"); return; }
    if (!gstin.session_active) {
      toast.error("GSTIN not authenticated — go to GST Reports → Authenticate first");
      return;
    }
    setLoading(true); setData(null);
    try {
      const params = { gstin: gstin.gstin };
      if (isAnnual) params.fy = fy;
      else if (mode === "period") params.period = period;
      else params.fy = fy;
      if (cfg.sections) params.section = section;
      const r = await api.get(`/gst-reports/${cfg.endpoint}`, { params });
      setData(r.data);
      toast.success("Fetched from gst.gov.in");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fetch failed — check GSTN portal or authenticate GSTIN");
    }
    setLoading(false);
  };

  return (
    <div className="p-6 space-y-6" data-testid={`compliance-${returnKey}`}>
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <ShieldCheck className="w-8 h-8 text-emerald-500" />
          {cfg.title}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Live from gst.gov.in through Zenith360-GST · check the GSTIN's portal login at{" "}
          <a href="/app/reports/gst-reports" className="underline">GST Reports</a>.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle>Fetch</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div className="md:col-span-2">
            <GstinSelector value={gstin} onChange={setGstin} />
          </div>
          {!isAnnual && (
            <div>
              <Label>Mode</Label>
              <select value={mode} onChange={(e) => setMode(e.target.value)}
                        className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                        data-testid="input-mode">
                <option value="period">Specific Month</option>
                <option value="fy">Full FY</option>
              </select>
            </div>
          )}
          {!isAnnual && mode === "period" ? (<>
            <div>
              <Label>Month</Label>
              <select className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                        value={period.slice(0, 2)}
                        onChange={(e) => setPeriod(e.target.value + period.slice(2, 6))}
                        data-testid="input-month">
                {MONTHS.map(m => <option key={m.v} value={m.v}>{m.n}</option>)}
              </select>
            </div>
            <div>
              <Label>Year</Label>
              <select className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                        value={period.slice(2, 6)}
                        onChange={(e) => setPeriod(period.slice(0, 2) + e.target.value)}
                        data-testid="input-year">
                {Array.from({ length: 7 }, (_, i) => String(new Date().getFullYear() - i)).map(y =>
                  <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </>) : (
            <div>
              <Label>Financial Year</Label>
              <select className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                        value={fy} onChange={(e) => setFy(e.target.value)}
                        data-testid="input-fy">
                {FY_LIST.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          )}
          {cfg.sections && (
            <div>
              <Label>Section</Label>
              <select value={section} onChange={(e) => setSection(e.target.value)}
                        className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                        data-testid="input-section">
                {cfg.sections.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
              </select>
            </div>
          )}
          <Button onClick={fetchIt} disabled={loading || !gstin?.session_active}
                    data-testid="btn-fetch">
            {loading ? "Fetching…" : "Fetch Report"}
          </Button>
        </CardContent>
      </Card>

      {data ? (
        <div className="p-6 rounded border bg-emerald-500/5 border-emerald-500/30
                          flex items-center gap-3" data-testid="fetch-status-card">
          <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" />
          <div className="text-sm">
            <div className="font-medium">Fetched from GSTN</div>
            <div className="text-muted-foreground text-xs mt-0.5">
              Response received. For downloadable Excel with this report,
              go to <a href="/app/reports/gst-reports" className="underline">GST Reports</a>.
            </div>
          </div>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground p-8 text-center border rounded"
               data-testid="empty-hint">
          <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-60" />
          Pick a GSTIN + period and click <b>Fetch Report</b>.
        </div>
      )}
    </div>
  );
}
